import React from 'react';
import { Link } from 'react-router-dom';
import { BlogPostMetadata } from '@/types/blog';
import { formatBlogDate, slugifyBlogTag } from '@/utils/blog';
import { FolderOpen, Tag } from 'lucide-react';

interface BlogCardProps {
  post: BlogPostMetadata;
  url: string;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, url }) => {
  return (
    <article
      className="overflow-hidden rounded-lg bg-gray-800 shadow-lg transition-transform hover:scale-[1.02] hover:shadow-xl focus-within:ring-2 focus-within:ring-sky-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-900"
    >
      <Link to={url} reloadDocument className="block relative w-full overflow-hidden bg-gray-900">
        <img 
          src={post.thumbnail} 
          alt={post.title} 
          className="w-full h-auto object-contain max-h-[240px]"
          loading="lazy"
        />
      </Link>
      
      <div className="p-5">
        <h2 className="mb-2 text-xl font-bold text-white">
          <Link to={url} reloadDocument className="hover:text-indigo-300 transition-colors">
            {post.title}
          </Link>
        </h2>
        <p className="mb-4 text-sm text-gray-400 line-clamp-3">{post.description}</p>
        
        <div className="mb-3 flex items-center justify-between text-xs text-gray-500">
          <span>{formatBlogDate(post.date)}</span>
          <span>{post.readingTime?.text || ''}</span>
        </div>

        {post.category && post.category !== 'Uncategorized' && (
          <div className="mb-3">
            <Link 
              to={`/blog/categories/${post.category.toLowerCase().replace(/\s+/g, '-')}`}
              className="inline-flex items-center rounded-full bg-sky-900/70 px-3 py-1 text-xs font-medium text-sky-200 hover:bg-sky-800/70 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1 focus:ring-offset-gray-800"
              aria-label={`Category: ${post.category}`}
            >
              <FolderOpen className="mr-1.5 h-3 w-3 flex-shrink-0" />
              {post.category}
            </Link>
          </div>
        )}
        
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <Link 
                to={`/blog/tags/${slugifyBlogTag(tag)}`}
                key={tag} 
                className="inline-flex items-center rounded-full bg-indigo-900/50 px-3 py-1 text-xs font-medium text-indigo-200 hover:bg-indigo-800/50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 focus:ring-offset-gray-800"
                aria-label={`Tag: ${tag}`}
              >
                <Tag className="mr-1.5 h-3 w-3 flex-shrink-0" />
                {tag}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogCard;
